import SessionStorage from './SessionStorage';

export const CLINIC_TIME_FORMAT_12_HOUR = '12-hour';
export const CLINIC_TIME_FORMAT_24_HOUR = '24-hour';
export const DEFAULT_CLINIC_TIME_FORMAT = CLINIC_TIME_FORMAT_24_HOUR;
export const SESSION_USER_KEY = 'my-user';

export const CLINIC_TIME_FORMAT_OPTIONS = [
  { value: CLINIC_TIME_FORMAT_24_HOUR, label: '24-Hour Format' },
  { value: CLINIC_TIME_FORMAT_12_HOUR, label: '12-Hour Format' },
];

export function parseClinicExtraInfo(extraInfo) {
  if (!extraInfo) return {};
  if (typeof extraInfo === 'object') return extraInfo;
  if (typeof extraInfo !== 'string') return {};

  try {
    const parsed = JSON.parse(extraInfo);

    if (typeof parsed === 'string') {
      try {
        return JSON.parse(parsed) || {};
      } catch (_error) {
        return {};
      }
    }

    return parsed || {};
  } catch (_error) {
    return {};
  }
}

export function normalizeClinicTimeFormat(value) {
  if (typeof value !== 'string') return '';
  const normalized = value.trim().toLowerCase();

  if ([
    '12-hour',
    '12 hour',
    '12-hour format',
    '12 hour format',
    '12h',
    '12hr',
    '12',
    '12_hour',
  ].includes(normalized)) {
    return CLINIC_TIME_FORMAT_12_HOUR;
  }

  if ([
    '24-hour',
    '24 hour',
    '24-hour format',
    '24 hour format',
    '24h',
    '24hr',
    '24',
    '24_hour',
  ].includes(normalized)) {
    return CLINIC_TIME_FORMAT_24_HOUR;
  }

  return '';
}

export function getClinicTimeFormatFromExtraInfo(extraInfo, fallback = '') {
  const parsed = parseClinicExtraInfo(extraInfo);
  return normalizeClinicTimeFormat(parsed?.time_format) || fallback;
}

export function getClinicTimeFormatFromClinic(clinic, fallback = '') {
  if (!clinic || typeof clinic !== 'object') {
    return fallback;
  }

  return getClinicTimeFormatFromExtraInfo(clinic?.extra_info, fallback);
}

export function getCurrentClinicTimeFormatFromUser(user, fallback = DEFAULT_CLINIC_TIME_FORMAT) {
  const directUserFormat = normalizeClinicTimeFormat(user?.current_clinic_time_format);
  if (directUserFormat) {
    return resolveClinicTimeFormat(directUserFormat);
  }

  const fromUserExtraInfo = getClinicTimeFormatFromExtraInfo(user?.current_clinic_extra_info, '');
  if (fromUserExtraInfo) {
    return resolveClinicTimeFormat(fromUserExtraInfo);
  }

  const currentClinic = user?.clinic || null;
  const fromCurrentClinic = getClinicTimeFormatFromClinic(currentClinic, '');
  if (fromCurrentClinic) {
    return resolveClinicTimeFormat(fromCurrentClinic);
  }

  const clinics = Array.isArray(user?.clinics) ? user.clinics : [];

  if (currentClinic?.id) {
    const matched = clinics.find(clinic => String(clinic?.id) === String(currentClinic.id));
    const matchedFormat = getClinicTimeFormatFromClinic(matched, '');
    if (matchedFormat) {
      return resolveClinicTimeFormat(matchedFormat);
    }
  }

  for (const clinic of clinics) {
    const format = getClinicTimeFormatFromClinic(clinic, '');
    if (format) {
      return resolveClinicTimeFormat(format);
    }
  }

  return resolveClinicTimeFormat(fallback);
}

export function getCurrentClinicTimeFormatFromSession(fallback = DEFAULT_CLINIC_TIME_FORMAT) {
  try {
    const user = SessionStorage.get(SESSION_USER_KEY);
    return getCurrentClinicTimeFormatFromUser(user, fallback);
  } catch (_error) {
    return resolveClinicTimeFormat(fallback);
  }
}

export function resolveClinicTimeFormat(timeFormat) {
  return normalizeClinicTimeFormat(timeFormat) || DEFAULT_CLINIC_TIME_FORMAT;
}

export function is12HourClinicTimeFormat(timeFormat) {
  return resolveClinicTimeFormat(timeFormat) === CLINIC_TIME_FORMAT_12_HOUR;
}
